import { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../hooks/useApi';
import { Badge } from '../components/ui';

// ─── Storage ───────────────────────────────────────────────────────────────────
// #3: accounts/distributors are now the typed commercial.NewBiz* tables, read and
// written via /api/new-business + /api/newbiz-* (the per-user blob is retired).

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fmtGSV(v) {
  if (!v) return '—';
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)     return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
}
function fmtNum(v) { return v != null ? v.toLocaleString() : '—'; }

const TODAY = new Date().toISOString().slice(0, 10);
function isOverdue(date) { return date && date < TODAY; }
function isDueSoon(date) {
  if (!date) return false;
  const d = new Date(date);
  const now = new Date();
  return d >= now && (d - now) / 86400000 <= 14;
}

// ─── Status helpers ────────────────────────────────────────────────────────────
const ACCOUNT_STATUSES   = ['Prospect', 'Stockist', 'In Progress', 'Not Suitable', 'On Hold'];
const DIST_STATUSES      = ['Prospect', 'Current', 'In Progress', 'Not Suitable', 'On Hold'];
const AU_STATES          = ['', 'NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT', 'National', 'International'];
const ALL_CHANNELS_DIST  = ['Retail', 'Foodservice', 'On Premise', 'Bottle Shops', 'Health Food', 'School Canteens', 'P&C'];
const ALL_REGIONS_DIST   = ['QLD', 'NSW', 'ACT', 'VIC', 'TAS', 'SA', 'WA', 'NT', 'International'];

function deriveAccountStatus(a) {
  if (a.status) return a.status;
  if (a.stockist && a.stockist.toLowerCase().includes('yes')) return 'Stockist';
  return 'Prospect';
}
function deriveDistStatus(d) {
  if (d.status) return d.status;
  if (d.current && (d.current.toLowerCase().includes('yes') || d.current.toLowerCase().includes('current'))) return 'Current';
  return 'Prospect';
}

const STATUS_COLORS = {
  Stockist: 'var(--success)', Current: 'var(--success)',
  'In Progress': 'var(--warn)',
  'Not Suitable': 'var(--danger)',
  'On Hold': 'var(--text-muted)',
  Prospect: 'var(--text-muted)',
};
function statusColor(s) { return STATUS_COLORS[s] || 'var(--text-muted)'; }

let _idSeq = 0;
function mkId() { return `nb-${Date.now()}-${++_idSeq}`; }

// ─── Shared UI atoms ──────────────────────────────────────────────────────────
const TH = { padding: '7px 10px', fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textAlign: 'left', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap', background: 'var(--bg-elevated)', position: 'sticky', top: 0, zIndex: 1 };
const TD = { padding: '7px 10px', fontSize: 12, color: 'var(--text-body)', borderBottom: '1px solid var(--bg-elevated)', verticalAlign: 'top' };

function BrandFit({ score }) {
  if (!score) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  const color = score >= 8 ? 'var(--success)' : score >= 5 ? 'var(--warn)' : 'var(--danger)';
  const pct = (score / 10) * 100;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ width: 40, height: 5, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 3 }} />
      </div>
      <span style={{ fontSize: 11, color, fontWeight: 600 }}>{score}</span>
    </div>
  );
}

function FollowUp({ date }) {
  if (!date) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  const od = isOverdue(date), soon = isDueSoon(date);
  return (
    <span style={{ fontSize: 11, color: od ? 'var(--danger)' : soon ? 'var(--warn)' : 'var(--text-muted)', fontWeight: od ? 700 : 400 }}>
      {od ? '⚠ ' : soon ? '→ ' : ''}{date}
    </span>
  );
}

// Badge now imported from ../components/ui — the shared primitive unifies the
// 14%-bg / 30%-border treatment with CRM.jsx (previously this file used 14%/35%
// — minor drift that the shared component eliminates).

function Pill({ text, active, onClick }) {
  return (
    <button onClick={onClick} className={active ? 'pill-active' : 'pill-inactive'} style={{ fontSize: 11 }}>
      {text}
    </button>
  );
}

function IconBtn({ onClick, title, children, danger }) {
  return (
    <button onClick={e => { e.stopPropagation(); onClick(); }} title={title}
      style={{ background: danger ? 'var(--danger-soft)' : 'var(--border)', border: `1px solid ${danger ? 'var(--danger-border)' : 'var(--border-soft)'}`, borderRadius: 'var(--radius-sm)', color: danger ? 'var(--danger)' : 'var(--text-body)', fontSize: 11, padding: '3px 7px', cursor: 'pointer', lineHeight: 1 }}>
      {children}
    </button>
  );
}

// ─── Edit form helpers ─────────────────────────────────────────────────────────
function EF({ label, value, onChange, type = 'text', options, rows }) {
  const base = { background: 'var(--bg-input)', border: '1px solid var(--border-soft)', borderRadius: 4, color: 'var(--text-primary)', fontSize: 12, padding: '4px 8px', width: '100%', boxSizing: 'border-box' };
  return (
    <div>
      <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 600, marginBottom: 3 }}>{label}</div>
      {options ? (
        <select value={value ?? ''} onChange={e => onChange(e.target.value)} style={base}>
          {options.map(o => <option key={o} value={o}>{o || '(none)'}</option>)}
        </select>
      ) : type === 'textarea' ? (
        <textarea value={value ?? ''} onChange={e => onChange(e.target.value)} rows={rows || 3}
          style={{ ...base, resize: 'vertical' }} />
      ) : (
        <input type={type} value={value ?? ''} onChange={e => onChange(e.target.value)} style={base} />
      )}
    </div>
  );
}

// ─── Account edit form ─────────────────────────────────────────────────────────
const BLANK_ACCOUNT = {
  brand: '', retailer_type: '', channel: '', state: '', geography: '', corp_mgmt: '',
  address: '', suburb: '', postcode: '', phone: '', website: '', facebook: '',
  locations: null, brand_fit: null, est_gsv: 0, supply_method: '',
  status: 'Prospect', stockist: '', date_onboard: '',
  contact_name: '', contact_title: '', contact_email: '', contact_phone: '',
  current_range: '', last_contact: '', next_followup: '', comments: '', blockers: '',
};

function AccountEditForm({ account, onSave, onDelete, onClose }) {
  const [form, setForm] = useState({ ...BLANK_ACCOUNT, ...account, status: deriveAccountStatus(account) });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const num = (k, v) => set(k, v === '' ? null : Number(v));

  return (
    <div style={{ padding: 16, background: 'var(--bg-elevated)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 10 }}>
        <EF label="Brand / Account *" value={form.brand} onChange={v => set('brand', v)} />
        <EF label="Status" value={form.status} onChange={v => set('status', v)} options={ACCOUNT_STATUSES} />
        <EF label="Channel" value={form.channel} onChange={v => set('channel', v)} />
        <EF label="Retailer Type" value={form.retailer_type} onChange={v => set('retailer_type', v)} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 10 }}>
        <EF label="State" value={form.state} onChange={v => set('state', v)} options={AU_STATES} />
        <EF label="Geography" value={form.geography} onChange={v => set('geography', v)} />
        <EF label="Supply Method" value={form.supply_method} onChange={v => set('supply_method', v)} />
        <EF label="Corp / Mgmt Group" value={form.corp_mgmt} onChange={v => set('corp_mgmt', v)} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 10 }}>
        <EF label="Locations" value={form.locations ?? ''} onChange={v => num('locations', v)} type="number" />
        <EF label="Brand Fit (0–10)" value={form.brand_fit ?? ''} onChange={v => num('brand_fit', v)} type="number" />
        <EF label="Est. GSV ($)" value={form.est_gsv ?? ''} onChange={v => num('est_gsv', v)} type="number" />
        <EF label="Date Onboarded" value={form.date_onboard} onChange={v => set('date_onboard', v)} type="date" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 10 }}>
        <EF label="Contact Name" value={form.contact_name} onChange={v => set('contact_name', v)} />
        <EF label="Contact Title" value={form.contact_title} onChange={v => set('contact_title', v)} />
        <EF label="Contact Email" value={form.contact_email} onChange={v => set('contact_email', v)} type="email" />
        <EF label="Contact Phone" value={form.contact_phone} onChange={v => set('contact_phone', v)} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 10 }}>
        <EF label="Website" value={form.website} onChange={v => set('website', v)} />
        <EF label="Phone (main)" value={form.phone} onChange={v => set('phone', v)} />
        <EF label="Next Follow-Up" value={form.next_followup} onChange={v => set('next_followup', v)} type="date" />
        <EF label="Last Contact" value={form.last_contact} onChange={v => set('last_contact', v)} type="date" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 10 }}>
        <EF label="Address" value={form.address} onChange={v => set('address', v)} />
        <EF label="Suburb" value={form.suburb} onChange={v => set('suburb', v)} />
        <EF label="Postcode" value={form.postcode} onChange={v => set('postcode', v)} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
        <EF label="Current Range" value={form.current_range} onChange={v => set('current_range', v)} />
        <EF label="Blockers" value={form.blockers} onChange={v => set('blockers', v)} />
      </div>
      <div style={{ marginBottom: 12 }}>
        <EF label="Comments / Next Steps" value={form.comments} onChange={v => set('comments', v)} type="textarea" rows={4} />
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <IconBtn onClick={onDelete} danger title="Delete account">Delete</IconBtn>
        <IconBtn onClick={onClose} title="Discard changes">Cancel</IconBtn>
        <button onClick={() => onSave({ ...form })}
          style={{ background: 'var(--info-soft)', border: '1px solid var(--info-border)', borderRadius: 6, color: 'var(--info)', fontSize: 12, padding: '5px 16px', cursor: 'pointer', fontWeight: 600 }}>
          Save
        </button>
      </div>
    </div>
  );
}

// ─── Distributor edit form ─────────────────────────────────────────────────────
const BLANK_CONTACT = { name: '', title: '', email: '', mobile: '', branch: '', state: '' };
const BLANK_DIST = {
  name: '', channels: [], regions: [], groups: [], area_covered: '', branch: '',
  current: '', status: 'Prospect', address: '', suburb: '', state: '', postcode: '',
  phone: '', website: '', email: '',
  competitor_range: '', comments: '', last_contact: '', next_followup: '',
  contacts: [],
};

function ContactRow({ contact, idx, onChange, onRemove }) {
  const set = (k, v) => onChange(idx, { ...contact, [k]: v });
  return (
    <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-soft)', borderRadius: 6, padding: 10, marginBottom: 8 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 6 }}>
        <EF label="Name" value={contact.name} onChange={v => set('name', v)} />
        <EF label="Title" value={contact.title} onChange={v => set('title', v)} />
        <EF label="Email" value={contact.email} onChange={v => set('email', v)} type="email" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
        <EF label="Mobile" value={contact.mobile} onChange={v => set('mobile', v)} />
        <EF label="Branch / Office" value={contact.branch} onChange={v => set('branch', v)} />
        <EF label="State" value={contact.state} onChange={v => set('state', v)} options={AU_STATES} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
        <IconBtn onClick={() => onRemove(idx)} danger title="Remove contact">Remove contact</IconBtn>
      </div>
    </div>
  );
}

function DistributorEditForm({ distributor, onSave, onDelete, onClose }) {
  const [form, setForm] = useState({
    ...BLANK_DIST, ...distributor,
    status: deriveDistStatus(distributor),
    contacts: distributor.contacts ? [...distributor.contacts] : [],
    _channels: (distributor.channels || []).join(', '),
    _regions:  (distributor.regions  || []).join(', '),
    _groups:   (distributor.groups   || []).join(', '),
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  function updateContact(idx, updated) { setForm(f => { const c = [...f.contacts]; c[idx] = updated; return { ...f, contacts: c }; }); }
  function removeContact(idx) { setForm(f => ({ ...f, contacts: f.contacts.filter((_, i) => i !== idx) })); }
  function addContact() { setForm(f => ({ ...f, contacts: [...f.contacts, { ...BLANK_CONTACT }] })); }

  function handleSave() {
    const out = {
      ...form,
      channels: form._channels.split(',').map(s => s.trim()).filter(Boolean),
      regions:  form._regions.split(',').map(s => s.trim()).filter(Boolean),
      groups:   form._groups.split(',').map(s => s.trim()).filter(Boolean),
    };
    delete out._channels; delete out._regions; delete out._groups;
    onSave(out);
  }

  return (
    <div style={{ padding: 16, background: 'var(--bg-elevated)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 10 }}>
        <EF label="Distributor Name *" value={form.name} onChange={v => set('name', v)} />
        <EF label="Status" value={form.status} onChange={v => set('status', v)} options={DIST_STATUSES} />
        <EF label="Area Covered" value={form.area_covered} onChange={v => set('area_covered', v)} />
        <EF label="Next Follow-Up" value={form.next_followup} onChange={v => set('next_followup', v)} type="date" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 10 }}>
        <div>
          <EF label="Channels (comma-separated)" value={form._channels} onChange={v => set('_channels', v)} />
          <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 3 }}>e.g. Retail, Foodservice, P&amp;C</div>
        </div>
        <div>
          <EF label="Regions (comma-separated)" value={form._regions} onChange={v => set('_regions', v)} />
          <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 3 }}>e.g. QLD, NSW, VIC</div>
        </div>
        <div>
          <EF label="Groups (comma-separated)" value={form._groups} onChange={v => set('_groups', v)} />
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 10 }}>
        <EF label="Website" value={form.website} onChange={v => set('website', v)} />
        <EF label="Email (general)" value={form.email} onChange={v => set('email', v)} type="email" />
        <EF label="Phone" value={form.phone} onChange={v => set('phone', v)} />
        <EF label="Last Contact" value={form.last_contact} onChange={v => set('last_contact', v)} type="date" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 10 }}>
        <EF label="Address" value={form.address} onChange={v => set('address', v)} />
        <EF label="Suburb" value={form.suburb} onChange={v => set('suburb', v)} />
        <EF label="State" value={form.state} onChange={v => set('state', v)} options={AU_STATES} />
        <EF label="Postcode" value={form.postcode} onChange={v => set('postcode', v)} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
        <EF label="Competitor Range" value={form.competitor_range} onChange={v => set('competitor_range', v)} />
        <EF label="Comments" value={form.comments} onChange={v => set('comments', v)} />
      </div>

      {/* Contacts */}
      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>
            Contacts ({form.contacts.length})
          </div>
          <button onClick={addContact}
            style={{ background: 'var(--info-soft)', border: '1px solid var(--info-border)', borderRadius: 4, color: 'var(--info)', fontSize: 11, padding: '3px 10px', cursor: 'pointer' }}>
            + Add Contact
          </button>
        </div>
        {form.contacts.length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic', marginBottom: 8 }}>No contacts — click "+ Add Contact" to add one.</div>
        )}
        {form.contacts.map((c, idx) => (
          <ContactRow key={idx} contact={c} idx={idx} onChange={updateContact} onRemove={removeContact} />
        ))}
      </div>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <IconBtn onClick={onDelete} danger title="Delete distributor">Delete</IconBtn>
        <IconBtn onClick={onClose} title="Discard changes">Cancel</IconBtn>
        <button onClick={handleSave}
          style={{ background: 'var(--info-soft)', border: '1px solid var(--info-border)', borderRadius: 6, color: 'var(--info)', fontSize: 12, padding: '5px 16px', cursor: 'pointer', fontWeight: 600 }}>
          Save
        </button>
      </div>
    </div>
  );
}

// ─── Key Accounts view ────────────────────────────────────────────────────────
function KeyAccounts({ accounts, editMode, onUpdate, onDelete, onAdd }) {
  const [search, setSearch]     = useState('');
  const [channel, setChannel]   = useState('All');
  const [state, setState]       = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [followup, setFollowup] = useState('All');
  const [sortBy, setSortBy]     = useState('brand_fit');
  const [sortDir, setSortDir]   = useState('desc');
  const [expanded, setExpanded] = useState(null);  // _id of expanded row
  const [page, setPage]         = useState(0);
  const PAGE_SIZE = 50;

  const channels = useMemo(() => ['All', ...new Set(accounts.map(a => a.channel).filter(Boolean))].sort(), [accounts]);
  const states   = useMemo(() => ['All', ...new Set(accounts.map(a => a.state).filter(Boolean))].sort(), [accounts]);

  const overdueAll = useMemo(() => accounts.filter(a => isOverdue(a.next_followup)), [accounts]);

  const filtered = useMemo(() => {
    let list = accounts;
    if (search) { const q = search.toLowerCase(); list = list.filter(a => a.brand.toLowerCase().includes(q) || (a.contact_name||'').toLowerCase().includes(q) || (a.channel||'').toLowerCase().includes(q)); }
    if (channel !== 'All') list = list.filter(a => a.channel === channel);
    if (state   !== 'All') list = list.filter(a => a.state   === state);
    if (statusFilter === 'Stockist')    list = list.filter(a => deriveAccountStatus(a) === 'Stockist');
    if (statusFilter === 'Prospect')    list = list.filter(a => deriveAccountStatus(a) === 'Prospect');
    if (statusFilter === 'Not Suitable') list = list.filter(a => deriveAccountStatus(a) === 'Not Suitable');
    if (statusFilter === 'In Progress') list = list.filter(a => deriveAccountStatus(a) === 'In Progress');
    if (followup === 'Overdue') list = list.filter(a => isOverdue(a.next_followup));
    if (followup === 'Soon')    list = list.filter(a => isDueSoon(a.next_followup));

    list = [...list].sort((a, b) => {
      let va = a[sortBy], vb = b[sortBy];
      if (va == null) va = sortDir === 'desc' ? -Infinity : Infinity;
      if (vb == null) vb = sortDir === 'desc' ? -Infinity : Infinity;
      if (typeof va === 'string') return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      return sortDir === 'asc' ? va - vb : vb - va;
    });
    return list;
  }, [accounts, search, channel, state, statusFilter, followup, sortBy, sortDir]);

  const paged = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const pages = Math.ceil(filtered.length / PAGE_SIZE);

  function toggleSort(col) {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('desc'); }
    setPage(0);
  }
  const sortIcon = col => sortBy === col ? (sortDir === 'desc' ? ' ↓' : ' ↑') : '';

  const stockists    = accounts.filter(a => deriveAccountStatus(a) === 'Stockist');
  const notSuitable  = accounts.filter(a => deriveAccountStatus(a) === 'Not Suitable');
  const pipelineGSV  = accounts.filter(a => deriveAccountStatus(a) !== 'Stockist' && a.est_gsv).reduce((s, a) => s + a.est_gsv, 0);
  const topFit       = accounts.filter(a => a.brand_fit >= 8);

  function handleAddNew() {
    const newAcc = { ...BLANK_ACCOUNT, _id: mkId(), brand: 'New Account' };
    onAdd(newAcc);
    setExpanded(newAcc._id);
    setPage(0);
    setSearch('');
    setStatusFilter('All');
    setChannel('All');
    setState('All');
  }

  return (
    <div>
      {/* KPI strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Total Accounts', value: fmtNum(accounts.length), sub: 'identified opportunities', color: 'var(--info)' },
          { label: 'Current Stockists', value: fmtNum(stockists.length), sub: `${((stockists.length / Math.max(accounts.length, 1)) * 100).toFixed(0)}% conversion rate`, color: 'var(--success)' },
          { label: 'Pipeline GSV', value: fmtGSV(pipelineGSV), sub: 'non-stockist estimate', color: 'var(--warn)' },
          { label: 'Brand Fit ≥ 8', value: fmtNum(topFit.length), sub: `${overdueAll.length} follow-ups overdue · ${notSuitable.length} not suitable`, color: 'var(--ai)' },
        ].map(k => (
          <div key={k.label} className="kpi-card">
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>{k.label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: k.color }}>{k.value}</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
        {editMode && (
          <button onClick={handleAddNew}
            style={{ background: 'var(--success-soft)', border: '1px solid var(--success-border)', borderRadius: 6, color: 'var(--success)', fontSize: 12, padding: '5px 14px', cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' }}>
            + Add Account
          </button>
        )}
        <input
          placeholder="Search brand, contact, channel…"
          value={search} onChange={e => { setSearch(e.target.value); setPage(0); }}
          style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-soft)', borderRadius: 6, color: 'var(--text-primary)', fontSize: 12, padding: '5px 10px', width: 220 }}
        />
        <div style={{ width: 1, height: 20, background: 'var(--border-soft)' }} />
        <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>Channel</span>
        <select value={channel} onChange={e => { setChannel(e.target.value); setPage(0); }}
          style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-soft)', borderRadius: 6, color: 'var(--text-primary)', fontSize: 12, padding: '4px 8px' }}>
          {channels.map(c => <option key={c}>{c}</option>)}
        </select>
        <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>State</span>
        <select value={state} onChange={e => { setState(e.target.value); setPage(0); }}
          style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-soft)', borderRadius: 6, color: 'var(--text-primary)', fontSize: 12, padding: '4px 8px' }}>
          {states.map(s => <option key={s}>{s}</option>)}
        </select>
        <div style={{ width: 1, height: 20, background: 'var(--border-soft)' }} />
        {[['All', 'All'], ['Stockist', 'Stockists'], ['Prospect', 'Prospects'], ['In Progress', 'In Progress'], ['Not Suitable', 'Not Suitable']].map(([v, l]) =>
          <Pill key={v} text={l} active={statusFilter === v} onClick={() => { setStatusFilter(v); setPage(0); }} />
        )}
        <div style={{ width: 1, height: 20, background: 'var(--border-soft)' }} />
        {[['All', 'All dates'], ['Overdue', `Overdue (${overdueAll.length})`], ['Soon', 'Due <14d']].map(([v, l]) =>
          <Pill key={v} text={l} active={followup === v} onClick={() => { setFollowup(v); setPage(0); }} />
        )}
        <span style={{ marginLeft: 'auto', color: 'var(--text-muted)', fontSize: 12 }}>{filtered.length} results</span>
      </div>

      {/* Table */}
      <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid var(--border)' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: editMode ? 960 : 900 }}>
          <thead>
            <tr>
              {editMode && <th style={{ ...TH, width: 32 }}></th>}
              <th style={TH}>Brand / Account</th>
              <th style={TH}>Channel</th>
              <th style={TH}>State</th>
              <th style={{ ...TH, cursor: 'pointer' }} onClick={() => toggleSort('locations')}>Locations{sortIcon('locations')}</th>
              <th style={{ ...TH, cursor: 'pointer' }} onClick={() => toggleSort('brand_fit')}>Brand Fit{sortIcon('brand_fit')}</th>
              <th style={{ ...TH, cursor: 'pointer' }} onClick={() => toggleSort('est_gsv')}>Est. GSV{sortIcon('est_gsv')}</th>
              <th style={TH}>Supply</th>
              <th style={TH}>Status</th>
              <th style={{ ...TH, cursor: 'pointer' }} onClick={() => toggleSort('next_followup')}>Follow-Up{sortIcon('next_followup')}</th>
              <th style={TH}>Contact</th>
            </tr>
          </thead>
          <tbody>
            {paged.map((a, i) => {
              const isExp = expanded === a._id;
              const acctStatus = deriveAccountStatus(a);
              return [
                <tr key={a._id} onClick={() => setExpanded(isExp ? null : a._id)}
                  style={{ cursor: 'pointer', background: isExp ? 'var(--border)' : (i % 2 === 0 ? 'var(--bg-elevated)' : 'var(--bg-elevated)') }}>
                  {editMode && (
                    <td style={{ ...TD, padding: '7px 6px' }} onClick={e => e.stopPropagation()}>
                      <IconBtn onClick={() => { if (window.confirm(`Delete "${a.brand}"?`)) onDelete(a._id); }} danger title="Delete">✕</IconBtn>
                    </td>
                  )}
                  <td style={{ ...TD, color: 'var(--text-primary)', fontWeight: 600 }}>
                    {a.brand}
                    {a.retailer_type && <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>{a.retailer_type}</div>}
                  </td>
                  <td style={TD}><Badge text={a.channel} color="var(--text-muted)" /></td>
                  <td style={TD}>{a.state || '—'}</td>
                  <td style={{ ...TD, textAlign: 'right' }}>{a.locations != null ? a.locations.toLocaleString() : '—'}</td>
                  <td style={TD}><BrandFit score={a.brand_fit} /></td>
                  <td style={{ ...TD, color: 'var(--warn)', fontWeight: 500 }}>{fmtGSV(a.est_gsv)}</td>
                  <td style={TD}><span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{a.supply_method || '—'}</span></td>
                  <td style={TD}><Badge text={acctStatus} color={statusColor(acctStatus)} /></td>
                  <td style={TD}><FollowUp date={a.next_followup} /></td>
                  <td style={{ ...TD, fontSize: 11 }}>
                    {a.contact_name || '—'}
                    {a.contact_title && <div style={{ color: 'var(--text-muted)', fontSize: 10 }}>{a.contact_title}</div>}
                  </td>
                </tr>,

                isExp && (editMode ? (
                  <tr key={`${a._id}-edit`}>
                    <td colSpan={editMode ? 11 : 10} style={{ padding: 0 }}>
                      <AccountEditForm
                        account={a}
                        onSave={updated => { onUpdate(a._id, updated); setExpanded(null); }}
                        onDelete={() => { if (window.confirm(`Delete "${a.brand}"?`)) { onDelete(a._id); setExpanded(null); } }}
                        onClose={() => setExpanded(null)}
                      />
                    </td>
                  </tr>
                ) : (
                  <tr key={`${a._id}-exp`} style={{ background: 'var(--border)' }}>
                    <td colSpan={10} style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, fontSize: 12 }}>
                        <div>
                          <div style={{ color: 'var(--text-muted)', fontSize: 11, marginBottom: 4, fontWeight: 600, textTransform: 'uppercase' }}>Details</div>
                          {a.address && <div style={{ color: 'var(--text-body)' }}>{a.address}, {a.suburb} {a.state} {a.postcode}</div>}
                          {a.website && <div style={{ color: 'var(--info)' }}>{a.website}</div>}
                          {a.phone && <div style={{ color: 'var(--text-muted)' }}>{a.phone}</div>}
                          {a.geography && <div style={{ color: 'var(--text-muted)' }}>Geography: {a.geography}</div>}
                          {a.corp_mgmt && <div style={{ color: 'var(--text-muted)' }}>Corp: {a.corp_mgmt}</div>}
                        </div>
                        <div>
                          <div style={{ color: 'var(--text-muted)', fontSize: 11, marginBottom: 4, fontWeight: 600, textTransform: 'uppercase' }}>Contact</div>
                          {a.contact_name  && <div style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{a.contact_name}</div>}
                          {a.contact_title && <div style={{ color: 'var(--text-muted)' }}>{a.contact_title}</div>}
                          {a.contact_email && <div style={{ color: 'var(--info)' }}>{a.contact_email}</div>}
                          {a.contact_phone && <div style={{ color: 'var(--text-muted)' }}>{a.contact_phone}</div>}
                          {a.date_onboard  && <div style={{ color: 'var(--success)', marginTop: 4 }}>Onboarded: {a.date_onboard}</div>}
                          {a.last_contact  && <div style={{ color: 'var(--text-muted)', marginTop: 4 }}>Last contact: {a.last_contact}</div>}
                        </div>
                        <div>
                          {a.comments && (
                            <>
                              <div style={{ color: 'var(--text-muted)', fontSize: 11, marginBottom: 4, fontWeight: 600, textTransform: 'uppercase' }}>Comments & Next Steps</div>
                              <div style={{ color: 'var(--text-body)', whiteSpace: 'pre-wrap' }}>{a.comments}</div>
                            </>
                          )}
                          {a.current_range && <div style={{ marginTop: 8 }}><span style={{ color: 'var(--text-muted)', fontSize: 11 }}>Current range: </span><span style={{ color: 'var(--text-body)' }}>{a.current_range}</span></div>}
                          {a.blockers && <div style={{ marginTop: 8, color: 'var(--danger)', fontSize: 11 }}>Blockers: {a.blockers}</div>}
                        </div>
                      </div>
                    </td>
                  </tr>
                )),
              ];
            })}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 16, alignItems: 'center' }}>
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
            style={{ background: 'var(--border)', border: '1px solid var(--border-soft)', borderRadius: 6, color: 'var(--text-body)', fontSize: 12, padding: '4px 12px', cursor: page === 0 ? 'default' : 'pointer', opacity: page === 0 ? 0.4 : 1 }}>
            ← Prev
          </button>
          <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>Page {page + 1} of {pages} · {filtered.length} results</span>
          <button onClick={() => setPage(p => Math.min(pages - 1, p + 1))} disabled={page === pages - 1}
            style={{ background: 'var(--border)', border: '1px solid var(--border-soft)', borderRadius: 6, color: 'var(--text-body)', fontSize: 12, padding: '4px 12px', cursor: page === pages - 1 ? 'default' : 'pointer', opacity: page === pages - 1 ? 0.4 : 1 }}>
            Next →
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Distributors view ────────────────────────────────────────────────────────
function Distributors({ distributors, editMode, onUpdate, onDelete, onAdd }) {
  const [search, setSearch]   = useState('');
  const [region, setRegion]   = useState('All');
  const [channel, setChannel] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [followup, setFollowup] = useState('All');
  const [expanded, setExpanded] = useState(null);
  const [page, setPage]       = useState(0);
  const PAGE_SIZE = 50;

  const overdueAll = useMemo(() => distributors.filter(d => isOverdue(d.next_followup)), [distributors]);

  const filtered = useMemo(() => {
    let list = distributors;
    if (search) { const q = search.toLowerCase(); list = list.filter(d => d.name.toLowerCase().includes(q) || (d.area_covered||'').toLowerCase().includes(q) || (d.contact_name||'').toLowerCase().includes(q)); }
    if (region  !== 'All') list = list.filter(d => d.regions.includes(region));
    if (channel !== 'All') list = list.filter(d => d.channels.includes(channel));
    if (statusFilter === 'Current')      list = list.filter(d => deriveDistStatus(d) === 'Current');
    if (statusFilter === 'Prospect')     list = list.filter(d => deriveDistStatus(d) === 'Prospect');
    if (statusFilter === 'Not Suitable') list = list.filter(d => deriveDistStatus(d) === 'Not Suitable');
    if (statusFilter === 'In Progress')  list = list.filter(d => deriveDistStatus(d) === 'In Progress');
    if (followup === 'Overdue') list = list.filter(d => isOverdue(d.next_followup));
    if (followup === 'Soon')    list = list.filter(d => isDueSoon(d.next_followup));
    return [...list].sort((a, b) => a.name.localeCompare(b.name));
  }, [distributors, search, region, channel, statusFilter, followup]);

  const paged = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const pages = Math.ceil(filtered.length / PAGE_SIZE);

  const currentDist  = distributors.filter(d => deriveDistStatus(d) === 'Current');
  const notSuitable  = distributors.filter(d => deriveDistStatus(d) === 'Not Suitable');

  function handleAddNew() {
    const newDist = { ...BLANK_DIST, _id: mkId(), name: 'New Distributor', channels: [], regions: [], groups: [] };
    onAdd(newDist);
    setExpanded(newDist._id);
    setPage(0);
    setSearch('');
    setStatusFilter('All');
  }

  return (
    <div>
      {/* KPI strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Total Distributors', value: fmtNum(distributors.length), sub: 'in database', color: 'var(--info)' },
          { label: 'Current Distributors', value: fmtNum(currentDist.length), sub: 'active relationships', color: 'var(--success)' },
          { label: 'Prospects', value: fmtNum(distributors.length - currentDist.length - notSuitable.length), sub: 'potential new partners', color: 'var(--warn)' },
          { label: 'Follow-ups Overdue', value: fmtNum(overdueAll.length), sub: `${notSuitable.length} marked not suitable`, color: overdueAll.length > 0 ? 'var(--danger)' : 'var(--text-muted)' },
        ].map(k => (
          <div key={k.label} className="kpi-card">
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>{k.label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: k.color }}>{k.value}</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
        {editMode && (
          <button onClick={handleAddNew}
            style={{ background: 'var(--success-soft)', border: '1px solid var(--success-border)', borderRadius: 6, color: 'var(--success)', fontSize: 12, padding: '5px 14px', cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' }}>
            + Add Distributor
          </button>
        )}
        <input
          placeholder="Search distributor, area, contact…"
          value={search} onChange={e => { setSearch(e.target.value); setPage(0); }}
          style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-soft)', borderRadius: 6, color: 'var(--text-primary)', fontSize: 12, padding: '5px 10px', width: 240 }}
        />
        <div style={{ width: 1, height: 20, background: 'var(--border-soft)' }} />
        <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>Region</span>
        <select value={region} onChange={e => { setRegion(e.target.value); setPage(0); }}
          style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-soft)', borderRadius: 6, color: 'var(--text-primary)', fontSize: 12, padding: '4px 8px' }}>
          {['All', ...ALL_REGIONS_DIST].map(r => <option key={r}>{r}</option>)}
        </select>
        <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>Channel</span>
        <select value={channel} onChange={e => { setChannel(e.target.value); setPage(0); }}
          style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-soft)', borderRadius: 6, color: 'var(--text-primary)', fontSize: 12, padding: '4px 8px' }}>
          {['All', ...ALL_CHANNELS_DIST].map(c => <option key={c}>{c}</option>)}
        </select>
        <div style={{ width: 1, height: 20, background: 'var(--border-soft)' }} />
        {[['All', 'All'], ['Current', 'Current'], ['Prospect', 'Prospects'], ['In Progress', 'In Progress'], ['Not Suitable', 'Not Suitable']].map(([v, l]) =>
          <Pill key={v} text={l} active={statusFilter === v} onClick={() => { setStatusFilter(v); setPage(0); }} />
        )}
        <div style={{ width: 1, height: 20, background: 'var(--border-soft)' }} />
        {[['All', 'All dates'], ['Overdue', `Overdue (${overdueAll.length})`], ['Soon', 'Due <14d']].map(([v, l]) =>
          <Pill key={v} text={l} active={followup === v} onClick={() => { setFollowup(v); setPage(0); }} />
        )}
        <span style={{ marginLeft: 'auto', color: 'var(--text-muted)', fontSize: 12 }}>{filtered.length} results</span>
      </div>

      {/* Table */}
      <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid var(--border)' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 900 }}>
          <thead>
            <tr>
              {editMode && <th style={{ ...TH, width: 32 }}></th>}
              <th style={TH}>Distributor</th>
              <th style={TH}>Channels</th>
              <th style={TH}>Regions</th>
              <th style={TH}>Groups</th>
              <th style={TH}>Status</th>
              <th style={TH}>Contact</th>
              <th style={TH}>Follow-Up</th>
              <th style={TH}>Area</th>
            </tr>
          </thead>
          <tbody>
            {paged.map((d, i) => {
              const isExp = expanded === d._id;
              const distStatus = deriveDistStatus(d);
              return [
                <tr key={d._id} onClick={() => setExpanded(isExp ? null : d._id)}
                  style={{ cursor: 'pointer', background: isExp ? 'var(--border)' : (i % 2 === 0 ? 'var(--bg-elevated)' : 'var(--bg-elevated)') }}>
                  {editMode && (
                    <td style={{ ...TD, padding: '7px 6px' }} onClick={e => e.stopPropagation()}>
                      <IconBtn onClick={() => { if (window.confirm(`Delete "${d.name}"?`)) onDelete(d._id); }} danger title="Delete">✕</IconBtn>
                    </td>
                  )}
                  <td style={{ ...TD, color: 'var(--text-primary)', fontWeight: 600 }}>{d.name}</td>
                  <td style={TD}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                      {(d.channels||[]).map(c => <Badge key={c} text={c} color="var(--info)" />)}
                    </div>
                  </td>
                  <td style={TD}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                      {(d.regions||[]).map(r => <Badge key={r} text={r} color="var(--text-muted)" />)}
                    </div>
                  </td>
                  <td style={TD}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                      {(d.groups||[]).map(g => <Badge key={g} text={g} color="var(--ai)" />)}
                    </div>
                  </td>
                  <td style={TD}><Badge text={distStatus} color={statusColor(distStatus)} /></td>
                  <td style={{ ...TD, fontSize: 11 }}>
                    {(d.contacts && d.contacts.length > 0) ? (
                      d.contacts.length === 1
                        ? <>{d.contacts[0].name}{d.contacts[0].title && <div style={{ color: 'var(--text-muted)', fontSize: 10 }}>{d.contacts[0].title}</div>}</>
                        : <span style={{ color: 'var(--info)' }}>{d.contacts.length} contacts</span>
                    ) : '—'}
                  </td>
                  <td style={TD}><FollowUp date={d.next_followup} /></td>
                  <td style={{ ...TD, fontSize: 11, color: 'var(--text-muted)' }}>{d.area_covered || d.state || '—'}</td>
                </tr>,

                isExp && (editMode ? (
                  <tr key={`${d._id}-edit`}>
                    <td colSpan={editMode ? 9 : 8} style={{ padding: 0 }}>
                      <DistributorEditForm
                        distributor={d}
                        onSave={updated => { onUpdate(d._id, updated); setExpanded(null); }}
                        onDelete={() => { if (window.confirm(`Delete "${d.name}"?`)) { onDelete(d._id); setExpanded(null); } }}
                        onClose={() => setExpanded(null)}
                      />
                    </td>
                  </tr>
                ) : (
                  <tr key={`${d._id}-exp`} style={{ background: 'var(--border)' }}>
                    <td colSpan={8} style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: 16, fontSize: 12 }}>
                        <div>
                          <div style={{ color: 'var(--text-muted)', fontSize: 11, marginBottom: 4, fontWeight: 600, textTransform: 'uppercase' }}>Details</div>
                          {d.address && <div style={{ color: 'var(--text-body)' }}>{d.address}, {d.suburb} {d.state} {d.postcode}</div>}
                          {d.phone && <div style={{ color: 'var(--text-muted)', marginTop: 2 }}>{d.phone}</div>}
                          {d.website && <div style={{ color: 'var(--info)', marginTop: 2 }}>{d.website}</div>}
                          {d.email && <div style={{ color: 'var(--info)', marginTop: 2 }}>{d.email}</div>}
                          {d.last_contact && <div style={{ color: 'var(--text-muted)', marginTop: 6 }}>Last contact: {d.last_contact}</div>}
                          {d.competitor_range && <div style={{ color: 'var(--warn)', marginTop: 4 }}>Competes: {d.competitor_range}</div>}
                          {d.comments && <div style={{ color: 'var(--text-body)', marginTop: 8, whiteSpace: 'pre-wrap' }}>{d.comments}</div>}
                        </div>
                        <div>
                          <div style={{ color: 'var(--text-muted)', fontSize: 11, marginBottom: 6, fontWeight: 600, textTransform: 'uppercase' }}>
                            Contacts ({(d.contacts || []).length})
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: (d.contacts||[]).length > 4 ? 'repeat(2, 1fr)' : '1fr', gap: 8 }}>
                            {(d.contacts || []).map((c, ci) => (
                              <div key={ci} style={{ background: 'var(--bg-elevated)', borderRadius: 5, padding: '7px 10px', border: '1px solid var(--border)' }}>
                                <div style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{c.name}</div>
                                {c.title  && <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>{c.title}</div>}
                                {c.email  && <div style={{ color: 'var(--info)', fontSize: 11, marginTop: 2 }}>{c.email}</div>}
                                {c.mobile && <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>{c.mobile}</div>}
                                {c.branch && <div style={{ color: 'var(--text-muted)', fontSize: 10, marginTop: 2 }}>{c.branch}</div>}
                              </div>
                            ))}
                            {(d.contacts || []).length === 0 && <div style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>No contacts recorded</div>}
                          </div>
                        </div>
                        <div />
                      </div>
                    </td>
                  </tr>
                )),
              ];
            })}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 16, alignItems: 'center' }}>
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
            style={{ background: 'var(--border)', border: '1px solid var(--border-soft)', borderRadius: 6, color: 'var(--text-body)', fontSize: 12, padding: '4px 12px', cursor: page === 0 ? 'default' : 'pointer', opacity: page === 0 ? 0.4 : 1 }}>
            ← Prev
          </button>
          <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>Page {page + 1} of {pages} · {filtered.length} results</span>
          <button onClick={() => setPage(p => Math.min(pages - 1, p + 1))} disabled={page === pages - 1}
            style={{ background: 'var(--border)', border: '1px solid var(--border-soft)', borderRadius: 6, color: 'var(--text-body)', fontSize: 12, padding: '4px 12px', cursor: page === pages - 1 ? 'default' : 'pointer', opacity: page === pages - 1 ? 0.4 : 1 }}>
            Next →
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function NewBusiness() {
  const [tab, setTab]           = useState('accounts');
  const [editMode, setEditMode] = useState(false);
  const [saved, setSaved]       = useState(false);

  // #3: read the typed commercial.NewBiz* tables; write per-record to the typed
  // endpoints (replaces the per-user newbiz blob + the whole-blob debounced save).
  const apiFetch = useApi();
  const queryClient = useQueryClient();
  const { data: nbData, error: loadError } = useQuery({
    queryKey: ['new-business'],
    queryFn: async () => { const r = await apiFetch('/new-business'); if (!r.ok) throw new Error(`Failed to load new business: HTTP ${r.status}`); return r.json(); },
    refetchOnMount: 'always',
    retry: false,
  });
  const accounts = nbData?.accounts ?? null;
  const distributors = nbData?.distributors ?? null;
  const summary = nbData?.summary || {};
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['new-business'] });

  const isDbId = (v) => /^\d+$/.test(String(v ?? ''));
  const accMut = useMutation({
    mutationFn: async (rec) => {
      const put = isDbId(rec._id);
      const r = await apiFetch(put ? `/newbiz-account/${rec._id}` : '/newbiz-account', { method: put ? 'PUT' : 'POST', body: JSON.stringify(rec) });
      if (!r.ok) throw new Error(`Save failed: HTTP ${r.status}`);
      return r.json();
    },
    onSuccess: invalidate,
    onError: (e) => { console.error('[NewBiz] account save', e); },
  });
  const accDelMut = useMutation({ mutationFn: async (id) => { const r = await apiFetch(`/newbiz-account/${id}`, { method: 'DELETE' }); if (!r.ok) throw new Error(`Delete failed: HTTP ${r.status}`); return r.json(); }, onSuccess: invalidate });
  const distMut = useMutation({
    mutationFn: async (rec) => {
      const put = isDbId(rec._id);
      const r = await apiFetch(put ? `/newbiz-distributor/${rec._id}` : '/newbiz-distributor', { method: put ? 'PUT' : 'POST', body: JSON.stringify(rec) });
      if (!r.ok) throw new Error(`Save failed: HTTP ${r.status}`);
      return r.json();
    },
    onSuccess: invalidate,
    onError: (e) => { console.error('[NewBiz] distributor save', e); },
  });
  const distDelMut = useMutation({ mutationFn: async (id) => { const r = await apiFetch(`/newbiz-distributor/${id}`, { method: 'DELETE' }); if (!r.ok) throw new Error(`Delete failed: HTTP ${r.status}`); return r.json(); }, onSuccess: invalidate });

  function showSaved() { setSaved(true); setTimeout(() => setSaved(false), 2000); }

  // Account callbacks (typed endpoints; query invalidation refreshes the view)
  function updateAccount(id, updated) { accMut.mutate({ ...updated, _id: id }, { onSuccess: showSaved }); }
  function deleteAccount(id) { accDelMut.mutate(id); }
  function addAccount(blank) { accMut.mutate({ ...blank, _id: undefined }, { onSuccess: showSaved }); }

  // Distributor callbacks
  function updateDistributor(id, updated) { distMut.mutate({ ...updated, _id: id }, { onSuccess: showSaved }); }
  function deleteDistributor(id) { distDelMut.mutate(id); }
  function addDistributor(blank) { distMut.mutate({ ...blank, _id: undefined }, { onSuccess: showSaved }); }

  function handleReset() {
    // Reload from the database. The typed tables are the source of truth now —
    // there is no separate "base vs edits" layer to revert to.
    invalidate();
  }

  function handleExport() {
    const blob = new Blob([JSON.stringify({ accounts, distributors, summary }, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'newbiz-export.json'; a.click();
    URL.revokeObjectURL(url);
  }

  if (!accounts) return <div style={{ color: 'var(--text-muted)', padding: 32 }}>Loading new business data…</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Load-failure banner — silent-fail bug fix (Phase 15). */}
      {loadError && <div style={{ background: 'var(--danger-soft)', border: '1px solid var(--danger-border)', borderRadius: 6, color: 'var(--danger)', padding: '8px 12px', fontSize: 13 }}>Failed to load new business: {String(loadError.message || loadError)}</div>}
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ color: 'var(--text-primary)', fontSize: 15, fontWeight: 600, margin: 0 }}>New Business Opportunities</h2>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
            {accounts.length} key accounts · {distributors.length} distributors
            {saved && <span style={{ marginLeft: 8, color: 'var(--success)' }}>· saved</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {saved && <span style={{ fontSize: 12, color: 'var(--success)' }}>Saved ✓</span>}
          {editMode && (
            <>
              <button onClick={handleReset}
                style={{ background: 'var(--danger-soft)', border: '1px solid var(--danger-border)', borderRadius: 6, color: 'var(--danger)', fontSize: 12, padding: '5px 12px', cursor: 'pointer' }}>
                Reset to Original
              </button>
              <button onClick={handleExport}
                style={{ background: 'var(--border)', border: '1px solid var(--border-soft)', borderRadius: 6, color: 'var(--text-body)', fontSize: 12, padding: '5px 12px', cursor: 'pointer' }}>
                Export JSON
              </button>
            </>
          )}
          <button onClick={() => setEditMode(m => !m)}
            style={{ background: editMode ? 'var(--warn-soft)' : 'var(--border)', border: `1px solid ${editMode ? 'var(--warn-border)' : 'var(--border-soft)'}`, borderRadius: 6, color: editMode ? 'var(--warn-text)' : 'var(--text-body)', fontSize: 12, padding: '5px 14px', cursor: 'pointer', fontWeight: editMode ? 600 : 400 }}>
            {editMode ? '✎ Editing' : '✎ Edit'}
          </button>
        </div>
      </div>

      {editMode && (
        <div style={{ background: 'var(--warn-soft)', border: '1px solid var(--warn-border)', borderRadius: 6, padding: '8px 14px', fontSize: 12, color: 'var(--warn-text)' }}>
          Edit mode — click any row to expand and edit. Use ✕ to delete. Changes save to the shared database.
        </div>
      )}

      {/* Sub-tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--border)' }}>
        {[
          { id: 'accounts',     label: `Key Accounts (${accounts.length})` },
          { id: 'distributors', label: `Distributors (${distributors.length})` },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={tab === t.id ? 'tab-active' : 'tab-inactive'}
            style={{ padding: '8px 18px', background: 'none', cursor: 'pointer', fontSize: 13, fontWeight: tab === t.id ? 600 : 400 }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'accounts' && (
        <KeyAccounts
          accounts={accounts}
          editMode={editMode}
          onUpdate={updateAccount}
          onDelete={deleteAccount}
          onAdd={addAccount}
        />
      )}
      {tab === 'distributors' && (
        <Distributors
          distributors={distributors}
          editMode={editMode}
          onUpdate={updateDistributor}
          onDelete={deleteDistributor}
          onAdd={addDistributor}
        />
      )}
    </div>
  );
}
